// @ts-nocheck
/* eslint-disable */
import { useRouteMatch } from 'react-router-dom';
import { Routes } from 'routes';
import { Brand } from 'common/features/store/duck/search-generic/utils/searchResultBrandsToBrands';
import React, { useCallback, useState, useRef, useEffect } from 'react';
import { AzureSearchBar } from './AzureSearchBar';
import { debounce } from 'lodash';
import { highlightMatchesHtml } from '../../utils';
import { constructorClient } from 'third-party-integrations/constructor';
import { useSelector, useDispatch } from 'react-redux';
import { useRootSelector } from 'rootStore';
import { convertConstructorItemToBrand } from 'common/utils/constructor';
import { selectLoginState, selectUserId } from 'common/features/store/duck/account/duck';
import { selectIsConstructorSearchEnabledWeb } from 'common/features/store/duck/ffs';
import { colors } from 'styles/settings';
import {
    applyTerm,
    selectRecentSearchTerms,
    selectSearchAppliedTerm,
    addRecentSearchTermsWebOnly,
} from 'common/features/store/duck/search/duck';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimesCircle } from '@fortawesome/pro-regular-svg-icons';
import { useConstructorDefaultParameters } from 'common/api/constructor/useConstructorDefaultParameters';
import { saveRecentSearchesToLocalStorage } from '../../features/search/components/utils';
import RemoteConfigWeb from 'config/RemoteConfigWeb';
import { createStyles } from 'utils/createStyle';
import { useNavMenu } from 'components/nav/context';
import { isBrandNew } from 'common/api/constructor/util';

export interface SearchBarProps {
    onSearch: (filter: string) => void;
    onItemSelected: (item: Brand) => void;
    maxAutocompleteItems?: number;
    className?: string;
}

const styles = createStyles({
    inputField: [
        'text-grey-medium relative inline-block w-full overflow-hidden focus:outline-none px-4 py-2',
        { '::placeholder': { color: colors.mediumGrey } },
    ],
});

export const SearchBar = ({ ...props }: SearchBarProps) => {
    const isUserLoggedIn = useRootSelector(selectLoginState);
    const isConstructorEnabled = useRootSelector(selectIsConstructorSearchEnabledWeb);

    if (isUserLoggedIn && isConstructorEnabled) {
        return <MySearchBar onSearch={props.onSearch} onItemSelected={props.onItemSelected} />;
    }
    return <AzureSearchBar {...props} />;
};

export const SearchService = {
    DefaultSearchTerm: '',
};

const useClearSearchWhenNotOnBrandsRoute = (
    setAutoCompleteResults?: (results: Brand[]) => void
) => {
    const dispatch = useDispatch<any>();
    const isBrandsRoute = useRouteMatch({
        path: Routes.Brands,
        sensitive: false,
        exact: false,
    });

    useEffect(() => {
        if (!isBrandsRoute) {
            SearchService.DefaultSearchTerm = '';
            dispatch(applyTerm(''));
            setAutoCompleteResults?.([]); // Clear stale autocomplete results
        }
    }, [isBrandsRoute, dispatch, setAutoCompleteResults]);
};

const useAutoCompleteControls = () => {
    const baseFilters = useConstructorDefaultParameters({ resultsPerSection: { Products: 8 } });
    const [autoCompleteResults, setAutoCompleteResults] = useState<Brand[]>([]);
    const isLoading = useRef(false);

    const getAutoCompleteResults = useCallback(
        debounce(async (searchText: string) => {
            if (!searchText) {
                setAutoCompleteResults([]);
                isLoading.current = false;
                return;
            }

            isLoading.current = true;
            const result = await constructorClient.autocomplete.getAutocompleteResults(
                searchText,
                baseFilters.current
            );

            // TODO: calling convertConstructorItemToBrand is _not_ required.
            const products = (result.sections['Products'] as []).map((result) => {
                const brand = convertConstructorItemToBrand(result);
                brand.isNew = isBrandNew(brand.newBannerExpiration);
                return brand;
            });
            isLoading.current = false;
            setAutoCompleteResults(products);
        }, 400),
        []
    );

    useEffect(() => {
        // NOTE: Not ideal -- in theory we should just cache the results
        // for now it's okay.
        // TODO: Use the redux-cache instead of doing an API call
        if (SearchService.DefaultSearchTerm) {
            getAutoCompleteResults(SearchService.DefaultSearchTerm);
        }
    }, [SearchService.DefaultSearchTerm]);

    return { isLoading, getAutoCompleteResults, autoCompleteResults, setAutoCompleteResults };
};

const useSearchInputFieldControls = (
    onSearch: SearchBarProps['onSearch'],
    getAutoCompleteResults: (_: string) => Promise<any>,
    userId: string
) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const appliedTerm = useSelector(selectSearchAppliedTerm);
    const dispatch = useDispatch<any>();
    const blockBlurring = useRef(false);
    const { closeNavMenu } = useNavMenu();
    useEffect(() => {
        // HACK: Mainly used to keep the inputRef value sync w/ the appliedTerm
        // _specifically_ when the input text is updated _outsided_ the searchbar.
        if (inputRef.current) {
            inputRef.current.value = appliedTerm;
            SearchService.DefaultSearchTerm = appliedTerm;
        }
    }, [appliedTerm]);

    const onChange = (e) => getAutoCompleteResults(e.target.value);

    const onBlur = (e) => {
        if (blockBlurring.current) {
            blockBlurring.current = false;
            return;
        }
        setTimeout(() => {
            setIsMenuOpen(false);
        }, 200);
    };

    const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        closeNavMenu(() => {});
        e.target.select();
        setIsMenuOpen(true);
    };

    const search = (e) => {
        if (e.key === 'Enter') {
            const value = e.target.value;
            SearchService.DefaultSearchTerm = value;
            onSearch(value);
            inputRef.current.blur();
            dispatch(addRecentSearchTermsWebOnly(userId, value, saveRecentSearchesToLocalStorage));
        }
    };

    const onClickSearch = (e) => {
        SearchService.DefaultSearchTerm = inputRef.current.value;
        onSearch(inputRef.current.value);
        dispatch(
            addRecentSearchTermsWebOnly(
                userId,
                SearchService.DefaultSearchTerm,
                saveRecentSearchesToLocalStorage
            )
        );
    };

    return {
        onBlur,
        onChange,
        onFocus,
        search,
        onClickSearch,
        inputRef,
        isMenuOpen,
        blockBlurring,
    };
};

interface DefaultAutoCompleteOptions {
    recentSearchTerms?: { label: string; options: { text: string; value: string }[] };
    brandsList?: { label: string; options: any[] };
}

const useDefaultAutoCompleteOptions = (userId: string) => {
    const MAX_NUMBER_OF_OPTIONS = 6;
    const baseFilters = useConstructorDefaultParameters({ itemIds: ' ' });
    const recentSearchTerms = useSelector(selectRecentSearchTerms);
    const recommendedBrands = useRef<{ label: string; options: any[] }>({ label: '', options: [] });
    const [defaultAutoCompleteOptions, setDefaultAutoCompleteOptions] =
        useState<DefaultAutoCompleteOptions>({});

    useEffect(() => {
        (async () => {
            try {
                const result = await constructorClient?.recommendations?.getRecommendations(
                    'search_zero_state_brands',
                    baseFilters.current
                );

                if (result.response.results.length) {
                    const userRecentSearchTerms = recentSearchTerms[userId] || [];
                    const numberOfSearchTerms =
                        userRecentSearchTerms.length >= 2 ? 2 : userRecentSearchTerms.length;

                    const results = result.response.results.slice(
                        0,
                        MAX_NUMBER_OF_OPTIONS - numberOfSearchTerms
                    );
                    recommendedBrands.current = {
                        label: 'Discover Featured Brands',
                        options: results.map((item) => {
                            const brand = convertConstructorItemToBrand(item);
                            brand.isSponsored = item.is_slotted;
                            brand.isNew = isBrandNew(brand.newBannerExpiration);
                            return brand;
                        }),
                    };
                    setDefaultAutoCompleteOptions((options) => {
                        return {
                            ...options,
                            brandsList: recommendedBrands.current,
                        };
                    });
                }
            } catch (e) {
                console.error('ERROR', e);
            }
        })();
    }, []);

    useEffect(() => {
        if (!recentSearchTerms) {
            return;
        }

        const userRecentSearchTerms = recentSearchTerms[userId] || [];

        if (userRecentSearchTerms.length) {
            const numberOfSearchTerms =
                userRecentSearchTerms.length >= 2 ? 2 : userRecentSearchTerms.length;
            setDefaultAutoCompleteOptions((options) => {
                const recentSearchTerms = {
                    label: 'Recent Searches',
                    options: userRecentSearchTerms
                        .slice(0, numberOfSearchTerms)
                        .map((searchTerm) => ({ text: searchTerm, value: searchTerm })),
                };
                let brandsList;
                if (options.brandsList) {
                    const opt = options.brandsList.options.slice(
                        0,
                        MAX_NUMBER_OF_OPTIONS - numberOfSearchTerms
                    );
                    brandsList = {
                        ...options.brandsList,
                        options: opt,
                    };
                }
                return {
                    ...options,
                    recentSearchTerms,
                    brandsList,
                };
            });
        }
    }, [recentSearchTerms]);

    return defaultAutoCompleteOptions;
};

const MySearchBar = ({
    onSearch,
    onItemSelected,
}: Pick<SearchBarProps, 'onSearch' | 'onItemSelected'>) => {
    const inputPlaceholder =
        RemoteConfigWeb.getRemoteValue('search_placeholder_text').asString() || 'Search for brand';
    const dispatch = useDispatch<any>();
    const userId = useSelector(selectUserId);
    const { getAutoCompleteResults, isLoading, autoCompleteResults, setAutoCompleteResults } =
        useAutoCompleteControls();
    const {
        onBlur,
        onChange,
        onFocus,
        search,
        onClickSearch,
        inputRef,
        isMenuOpen,
        blockBlurring,
    } = useSearchInputFieldControls(onSearch, getAutoCompleteResults, userId);
    const defaultAutoCompleteOptions = useDefaultAutoCompleteOptions(userId);
    useClearSearchWhenNotOnBrandsRoute(setAutoCompleteResults);

    const onClear = () => {
        // NOTE: We _only_ need to block blurring IFF
        // the menu is already opened to avoid flickering the dropdown
        // window.
        if (isMenuOpen) {
            blockBlurring.current = true;
        }

        setAutoCompleteResults([]);
        SearchService.DefaultSearchTerm = '';
        inputRef.current.value = SearchService.DefaultSearchTerm;
        dispatch(applyTerm(''));
        setTimeout(() => {
            inputRef.current.focus();
        }, 200);
    };

    const renderResults = () => {
        if (inputRef.current && inputRef.current.value && autoCompleteResults.length > 0) {
            return [
                <li
                    key={inputRef.current.value}
                    className="px-2 sm:px-6 py-2 no-underline text-left text-brand hover:bg-pale-blue-40 flex gap-2 items-center cursor-pointer pt-[20px] pb-[10px]"
                    onMouseDown={onClickSearch}
                >
                    <FontAwesomeIcon
                        color={colors.grey3}
                        icon={faSearch}
                        className="inline-block text-xl"
                    />
                    <p className="text-brand-dark font-semibold">
                        Search for{' '}
                        <span className="text-brand-dark font-normal text-base">
                            "{inputRef.current.value}"
                        </span>
                    </p>
                </li>,
                ...autoCompleteResults.map((item, index) => (
                    <ListItem
                        key={item.name}
                        searchTerm={inputRef.current.value}
                        label={item.name}
                        imageUrl={item.imageUrl}
                        id={item.id}
                        onClick={() => {
                            onItemSelected(item);
                        }}
                        isSponsored={item.isSponsored}
                        isNew={item.isNew}
                    />
                )),
            ];
        } else if (isLoading.current) {
            return (
                <li>
                    <span>Loading...</span>
                </li>
            );
        } else if (inputRef.current && inputRef.current.value && autoCompleteResults.length === 0) {
            return (
                <li
                    className={`px-4 py-2 no-underline capitalize text-center text-brand-dark grid items-center`}
                >
                    <span className="text-brand">No Results Found.</span>
                </li>
            );
        } else if (
            defaultAutoCompleteOptions.recentSearchTerms ||
            defaultAutoCompleteOptions.brandsList
        ) {
            const recentSearches = defaultAutoCompleteOptions?.recentSearchTerms?.options?.map(
                (item) => (
                    <li
                        key={item.value}
                        className="px-4 sm:px-6 py-2 no-underline capitalize text-left text-brand hover:bg-pale-blue-40 hover:underline focus:underline flex gap-2 items-center cursor-pointer"
                        onMouseDown={() => {
                            SearchService.DefaultSearchTerm = item.value;
                            onSearch(item.value);
                        }}
                    >
                        <FontAwesomeIcon
                            color={colors.grey3}
                            icon={faSearch}
                            className="inline-block text-xl"
                        />
                        <span className="text-brand-dark text-base">{item.value}</span>
                    </li>
                )
            );
            const brandsList = defaultAutoCompleteOptions?.brandsList?.options?.map(
                (item, index) => (
                    <ListItem
                        key={item.name}
                        searchTerm={inputRef.current.value}
                        label={item.name}
                        imageUrl={item.imageUrl}
                        id={item.id}
                        onClick={() => {
                            onItemSelected(item);
                        }}
                        isSponsored={item.isSponsored}
                        isNew={item.isNew}
                    />
                )
            );

            return (
                <>
                    {defaultAutoCompleteOptions?.recentSearchTerms?.options?.length ? (
                        <h2 className="px-4 sm:px-6 font-semibold text-base text-brand-dark my-2 mb-0 sm:mt-4 sm:mb-2">
                            {defaultAutoCompleteOptions?.recentSearchTerms?.label}
                        </h2>
                    ) : null}
                    {recentSearches}
                    {defaultAutoCompleteOptions?.brandsList?.options?.length ? (
                        <h2 className="px-4 sm:px-6 font-semibold text-base text-brand-dark mt-2 mb-0 sm:mt-4 sm:mb-2">
                            {defaultAutoCompleteOptions?.brandsList?.label}
                        </h2>
                    ) : null}
                    {brandsList}
                </>
            );
        } else {
            return (
                <li className="px-4 py-4 no-underline capitalize text-center text-grey-4">
                    <span>Type something to search for brands.</span>
                </li>
            );
        }
    };

    const containerRef = useRef<HTMLDivElement>(null);
    const width = containerRef.current ? containerRef.current.clientWidth : 0;

    return (
        <div ref={containerRef} className="max-w-md sm:max-w-xl w-full">
            <div className="flex relative max-w-md sm:max-w-xl w-full">
                <div className="flex items-center inline-block max-w-md sm:max-w-xl w-full rounded-full overflow-hidden border-solid border-brand border focus:outline-none px-2 py-1">
                    <input
                        data-cnstrc-search-input
                        defaultValue={SearchService.DefaultSearchTerm}
                        placeholder={inputPlaceholder}
                        ref={inputRef}
                        list="auto-complete-list"
                        className={styles.inputField}
                        type="text"
                        id="search-bar"
                        onKeyUp={search}
                        onChange={onChange}
                        onBlur={onBlur}
                        onFocus={onFocus}
                        autoComplete="off"
                        spellCheck="false"
                    />
                    {SearchService.DefaultSearchTerm ? (
                        <button
                            className="flex items-center text-brand hover:text-brand-dark pl-2 mr-2"
                            onMouseDown={onClear}
                            style={{ outline: 'none' }}
                        >
                            <FontAwesomeIcon
                                icon={faTimesCircle}
                                className="inline-block text-xl  "
                            />
                        </button>
                    ) : null}
                    <button
                        data-cnstrc-search-submit-btn
                        className="w-[46px] h-[38px] outline-none center border-solid rounded-full border-brand text-white bg-brand text-center outline-none hover:bg-brand-dark focus:bg-brand-dark hover:text-white focus:text-white"
                        onClick={onClickSearch}
                    >
                        <FontAwesomeIcon
                            icon={faSearch}
                            className="w-[18px] h-[18px] inline-block text-2xl"
                        />
                    </button>
                </div>
            </div>
            {isMenuOpen ? (
                <ul
                    data-cnstrc-autosuggest
                    style={{ width: width }}
                    className="border border-solid border-grey-light max-w-md sm:max-w-xl w-full rounded-md absolute bg-white z-40 mt-1 rounded-t-none shadow-md"
                >
                    {renderResults()}
                </ul>
            ) : null}
        </div>
    );
};

const ListItem = ({ id, searchTerm, label, imageUrl, onClick, isSponsored, isNew }) => {
    // highligh matches
    const highlighted = highlightMatchesHtml(label, searchTerm);
    // TODO: Add letter spacing for NEW badge letters
    const isNewStyles = 'border-[3px] border-orange flex flex-col';
    return (
        <li
            data-cnstrc-item-section="Products"
            data-cnstrc-item-name={label}
            className={`px-4 py-2 sm:px-6 sm:py-3 capitalize text-left text-brand-dark hover:bg-pale-blue-40 grid items-center cursor-pointer`}
            style={{ gridTemplateColumns: 'max-content auto' }}
            onMouseDown={onClick}
        >
            {imageUrl ? (
                <div
                    className={`relative bg-white border border-grey-light rounded-md px-2 py-4 ${
                        isNew ? isNewStyles : ''
                    }`}
                >
                    <img
                        width={68}
                        src={imageUrl}
                        className={`inline-block ${isNew ? 'mb-[10px]' : ''}`}
                    />
                    {isNew ? (
                        <span className="block absolute left-0 bottom-0 w-full text-xs text-white font-semibold bg-orange h-[14px] text-center">
                            NEW
                        </span>
                    ) : null}
                </div>
            ) : id ? (
                <div className="inline-block w-16 bg-grey-3" />
            ) : null}
            <div className="ml-2 overflow-hidden">
                {isSponsored ? (
                    <i className="no-underline text-xs text-grey-medium">Sponsored</i>
                ) : null}
                <p
                    className={
                        'truncate p-0 no-underline align-top hover:underline hover:text-brand leading-8 truncate w-full text-base'
                    }
                >
                    {highlighted}
                </p>
            </div>
        </li>
    );
};
